import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { addFightComment, getFightComments } from "../../http/auth";
import { Button } from "../ui/btns/Button";
import { toast } from "react-toastify";
import { formatDistanceToNow } from "date-fns";

const CommentItem = ({ comment, fightID, depth = 0, isUserVoted }) => {
  const queryClient = useQueryClient();
  const [replyText, setReplyText] = useState("");
  const [showReplyBox, setShowReplyBox] = useState(false);
  const [showReplies, setShowReplies] = useState(false);

  const replyMutation = useMutation({
    mutationFn: (payload) => addFightComment(payload),
    onSuccess: () => {
      toast.success("Reply added!");
      setReplyText("");
      setShowReplyBox(false);
      queryClient.invalidateQueries({ queryKey: ["comments", fightID] });
    },
    onError: () => toast.error("Failed to reply", { autoClose: 5000 }),
  });

  return (
    <div className="mb-5">
      {/* Username + Timestamp */}
      <div className="flex items-center gap-2 mb-1">
        <p className="text-sm font-semibold text-flyers-orange">
          {comment.UserDetails?.username || "Unknown"}
        </p>
        <span className="text-xs text-flyers-mid-gray">
          {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
        </span>
      </div>

      {/* Comment Text */}
      <p className="text-white/80 text-sm">{comment.text}</p>

      {/* Actions */}
      <div className="mt-2 flex gap-4">
        {isUserVoted && (
          <button
            className="text-xs font-medium text-flyers-orange hover:text-flyers-orange-light transition-colors"
            onClick={() => setShowReplyBox((prev) => !prev)}
          >
            {showReplyBox ? "Cancel" : "Reply"}
          </button>
        )}

        {comment.Replies?.length > 0 && (
          <button
            className="text-xs font-medium text-flyers-orange hover:text-flyers-orange-light transition-colors"
            onClick={() => setShowReplies((prev) => !prev)}
          >
            {showReplies ? "Hide replies" : `See replies (${comment.Replies.length})`}
          </button>
        )}
      </div>

      {/* Reply Input */}
      {showReplyBox && (
        <div className="mt-2 flex gap-2">
          <input
            type="text"
            placeholder="Write a reply..."
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            className="flex-1 px-3 py-1 text-sm text-white bg-flyers-charcoal rounded border border-flyers-dark-gray focus:border-flyers-orange focus:outline-none placeholder-flyers-mid-gray"
            onKeyDown={(e) => {
              if (
                e.key === "Enter" &&
                !replyMutation?.isPending &&
                replyText.trim() !== ""
              )
                replyMutation.mutate({
                  id: fightID,
                  text: replyText,
                  parentCommentId: comment.CommentID,
                });
            }}
          />
          <Button
            variant="primary"
            onClick={() =>
              replyMutation.mutate({
                id: fightID,
                text: replyText,
                parentCommentId: comment.CommentID,
              })
            }
            disabled={replyMutation.isPending || replyText?.trim() === ""}
          >
            Post
          </Button>
        </div>
      )}

      {/* Nested Replies */}
      {showReplies && comment.Replies?.length > 0 && (
        <div className="mt-3 ml-5 pl-4 border-l border-flyers-dark-gray space-y-3">
          {comment.Replies.map((reply) => (
            <CommentItem
              key={reply.CommentID}
              comment={reply}
              fightID={fightID}
              depth={depth + 1}
              isUserVoted={isUserVoted}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const CommentsSection = ({ fightID, isUserVoted }) => {
  const queryClient = useQueryClient();
  const [commentText, setCommentText] = useState("");

  const { data: commentsData, isLoading } = useQuery({
    queryKey: ["comments", fightID],
    queryFn: () => getFightComments(fightID),
  });

  const commentMutation = useMutation({
    mutationFn: addFightComment,
    onSuccess: () => {
      toast.success("Comment added!");
      setCommentText("");
      queryClient.invalidateQueries({ queryKey: ["comments", fightID] });
    },
    onError: () => toast.error("Failed to comment", { autoClose: 5000 }),
  });

  return (
    <div className="mt-10 max-w-2xl mx-auto w-full">
      <h2 className="text-2xl font-heading font-bold uppercase text-flyers-orange mb-6">Comments</h2>

      {/* Add new comment */}
      {isUserVoted && (
        <div className="mb-6 flex gap-2">
          <input
            type="text"
            placeholder="Write a comment..."
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            className="flex-1 px-4 py-2 text-white bg-flyers-charcoal rounded border border-flyers-dark-gray focus:border-flyers-orange focus:outline-none placeholder-flyers-mid-gray"
            onKeyDown={(e) => {
              if (
                e.key === "Enter" &&
                !commentMutation?.isPending &&
                commentText.trim() !== ""
              )
                commentMutation.mutate({
                  id: fightID,
                  text: commentText,
                  parentCommentId: null,
                });
            }}
          />
          <Button
            variant="primary"
            onClick={() =>
              commentMutation.mutate({
                id: fightID,
                text: commentText,
                parentCommentId: null,
              })
            }
            disabled={commentMutation.isPending || commentText?.trim() === ""}
          >
            Post
          </Button>
        </div>
      )}

      {!isUserVoted && (
        <p className="text-red-500 my-4">Please vote first in order to comment.</p>
      )}

      {/* Comments List */}
      <div className="space-y-6">
        {isLoading && <p className="text-white">Loading comments...</p>}
        {commentsData?.data?.map((c) => (
          <CommentItem
            key={c.CommentID}
            comment={c}
            fightID={fightID}
            isUserVoted={isUserVoted}
          />
        ))}
        {commentsData?.data?.length === 0 && !isLoading && (
          <p className="text-white">No comments yet. Be the first!</p>
        )}
      </div>
    </div>
  );
};

export default CommentsSection;
